/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';

import { Box, IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { Search } from '@mui/icons-material';

interface Props {
  onSearch: (value: string) => void;
  defaultValue?: string;
  /**
   * Bump this (e.g. on Refresh) to reset the input to `defaultValue` —
   * the committed/applied search — discarding unsubmitted edits.
   */
  resetKey?: string | number;
  /** When set, non-empty queries shorter than this cannot be submitted (search button disabled). */
  minSearchLength?: number;
}

export const SearchTextField = ({
  onSearch,
  defaultValue = '',
  resetKey,
  minSearchLength,
  disabled,
  error,
  helperText,
  ...props
}: Props & TextFieldProps) => {
  const [shrink, setShrink] = useState(!!defaultValue);
  const [searchQuery, setSearchQuery] = useState(defaultValue);

  useEffect(() => {
    if (resetKey === undefined) return;
    setSearchQuery(defaultValue);
    setShrink(!!defaultValue);
    // Only react to refresh/sync signals; defaultValue is read intentionally at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const trimmed = searchQuery.trim();
  const belowMinLength =
    minSearchLength != null && trimmed.length > 0 && trimmed.length < minSearchLength;
  const minLengthHelperText =
    belowMinLength && minSearchLength != null
      ? `Enter at least ${minSearchLength} characters to search.`
      : undefined;

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (belowMinLength) {
          return;
        }
        onSearch(searchQuery);
      }}
      sx={{ width: '100%' }}
    >
      <TextField
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        disabled={disabled}
        error={error || belowMinLength}
        helperText={minLengthHelperText ?? helperText}
        sx={{
          width: '100%',
          bgcolor: 'background.default',
          borderRadius: '51px',
          '& input': {
            pr: 2,
            py: 1.5,
          },
          '& .MuiInputBase-root': {
            borderRadius: '51px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'grey.400',
            px: 2,
            fontSize: (theme) => theme.typography.pxToRem(17.7),
          },
          '& .MuiInputLabel-root': {
            ml: 1,
            lineHeight: '20px',
            color: 'text.primary',
            maxWidth: ['calc(100% - 80px)', 'calc(100% - 120px)'],
          },
          ...props.sx,
        }}
        onFocus={() => setShrink(true)}
        onBlur={(e) => setShrink(!!e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="search"
                edge="end"
                type="submit"
                disabled={disabled || belowMinLength}
                sx={{
                  px: [1.5, 3.5],
                  backgroundColor: 'link.main',
                  color: 'link.contrastText',
                  py: 1,
                  borderRadius: '100px',
                  mr: -1.2,
                  '&:hover': {
                    backgroundColor: 'link.dark',
                  },
                }}
              >
                <Search />
              </IconButton>
            </InputAdornment>
          ),
          ...props.InputProps,
        }}
        InputLabelProps={{
          shrink,
          ...props.InputLabelProps,
        }}
        {...props}
      />
    </Box>
  );
};
